import React, { useEffect, useRef, useState } from "react";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { FixedSizeList } from "react-window";
import Item from "@/components/dragndrop/dndItem";
import { Row } from "@/components/dragndrop/dndRow";
import "@/components/dragndrop/formEditorStyles.css";
import styles from "@/components/dragndrop/dndItemList.module.css";
import { useTranslation } from "react-i18next";

interface Props {
  column?: any;
  preExistingFormElements?: any;
  onHandleCheckboxChange?: (id: string) => void;
  handleDeleteItem?: (id: string | undefined) => void;
  onHandleRulesetChange?: (event: any, id: any) => void;
  index?: any;
  leftColumnPlaceholderText?: string;
  onMoveItem?: (index: number, direction: 1 | -1) => void;
  showFieldPreview?: boolean;
  keySuggestions?: { value: string; label?: string }[];
  invalidRulesetItemId?: string | null;
  registerRulesetValidationTarget?: (
    itemId: string,
  ) => React.RefCallback<HTMLElement>;
}

export const ItemList = React.memo(function ItemList({
  column,
  preExistingFormElements,
  onHandleCheckboxChange,
  handleDeleteItem,
  onHandleRulesetChange,
  leftColumnPlaceholderText,
  onMoveItem,
  showFieldPreview,
  keySuggestions,
  invalidRulesetItemId,
  registerRulesetValidationTarget,
}: Props) {
  const listRef = useRef();
  const { t } = useTranslation();

  const measureRef = useRef<HTMLDivElement | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number>(600);

  useEffect(() => {
    const element = measureRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect?.height;
      if (height) {
        setMeasuredHeight(Math.floor(height));
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  if (column.id === "column-1") {
    return (
      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`${styles.assemblyList} ${
              snapshot.isDraggingOver ? styles.assemblyListDragOver : ""
            }`}
          >
            {(!column.items || column.items.length === 0) && (
              <div className={styles.emptyAssemblyList}>
                {t("drag_elements_here")}
              </div>
            )}
            {(column.items || []).map((item: any, index: number) => (
              <Draggable draggableId={item.name} index={index} key={item.id}>
                {(dragProvided, snapshot) => (
                  <Item
                    provided={dragProvided}
                    isDragging={snapshot.isDragging}
                    item={item}
                    columnId={column.id}
                    index={index}
                    itemCount={column.items.length}
                    handleDeleteItem={handleDeleteItem}
                    preExistingFormElements={preExistingFormElements}
                    onHandleCheckboxChange={onHandleCheckboxChange}
                    onHandleRulesetChange={onHandleRulesetChange}
                    onMoveItem={onMoveItem}
                    showFieldPreview={showFieldPreview}
                    keySuggestions={keySuggestions}
                    forceSettingsExpanded={invalidRulesetItemId === item.id}
                    rulesetValidationTargetRef={
                      registerRulesetValidationTarget?.(item.id)
                    }
                  />
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  }

  return (
    <div ref={measureRef} className={styles.searchListContainer}>
      <Droppable
        droppableId={column.id}
        mode="virtual"
        isDropDisabled={column.id === "column-0"}
        renderClone={(provided, snapshot, rubric) => (
          <Item
            provided={provided}
            isDragging={snapshot.isDragging}
            item={column.items[rubric.source.index]}
            columnId={column.id}
            style={
              snapshot.isDragging && rubric.source.droppableId === "column-0"
                ? {
                    borderRadius: "8px",
                    border: "solid lightgray",
                    alignItems: "center",
                    justifyContent: "center",
                  }
                : ""
            }
          />
        )}
      >
        {(provided, snapshot) => {
          const listHeight = measuredHeight;

          if (!column.items || column.items.length === 0) {
            return (
              <div
                ref={provided.innerRef}
                className={styles.emptyList}
                style={{ height: listHeight }}
              >
                {leftColumnPlaceholderText}
                {provided.placeholder}
              </div>
            );
          }

          const itemCount = snapshot.isUsingPlaceholder
            ? column.items.length + 1
            : column.items.length;

          return (
            <FixedSizeList
              overscanCount={250}
              height={listHeight}
              itemCount={itemCount}
              itemSize={75}
              width={"100%"}
              outerRef={provided.innerRef}
              itemData={
                {
                  data: column.items,
                  columnId: column.id,
                  handleDeleteItem,
                  preExistingFormElements,
                  onHandleCheckboxChange,
                  onHandleRulesetChange,
                } as any
              }
              className={styles.inputList}
              innerRef={listRef}
            >
              {Row}
            </FixedSizeList>
          );
        }}
      </Droppable>
    </div>
  );
});
